import { useCallback, useRef } from 'react';

import globalAxios, { AxiosRequestTransformer } from 'axios';

import { getOSName } from '@sbv-clients/util-misc';

import {
  EnvironmentProvider,
  useEnvironmentContext,
} from './context/environment';
import {
  AbsenceRequestsApi,
  AbsencesApi,
  AccountApi,
  AccrualsV2Api,
  AdminApi,
  AdminFacilityApi,
  AdminReportsApi,
  AttachmentsApi,
  AuthorizationApi,
  ChatApi,
  ClosePayPeriodV2Api,
  ComplianceApi,
  Configuration,
  ConfigurationsV2Api,
  ContactSupportV2Api,
  DashboardApi,
  DepartmentApi,
  EmployeeApi,
  EmployeeV2Api,
  EntityApi,
  FacilityApi,
  HourV2Api,
  HrApi,
  IndexManagementApi,
  ImportV2Api,
  LaborLevelApi,
  ProfileManagementApi,
  ManageSchedulesApi,
  MasterScheduleApi,
  MetaDataApi,
  MetricsApi,
  MobileAppVersionApi,
  MobileV2Api,
  OpeningTemplateApi,
  OpenShiftApi,
  PbjApi,
  PositionApi,
  ProLaborLevelDesignationApi,
  PunchRequestsV2Api,
  PunchV2Api,
  ReportsApi,
  SchedulePatternApi,
  ScheduleV2Api,
  ShiftTypeApi,
  TimeCardApi,
  TimeClockApi,
  SupportApi,
  ToolsV2Api,
  UnitApi,
  UserV2Api,
} from './lib';
import { SaaSReportsReportApi, TaReportsReportApi } from './report-lib';
import ResponseInterceptors from './response-interceptors';
import { getCurrentMicroSessionId } from './util/micro-session';

export * from './lib/api-models';
export * from './lib/api';
export * from './report-lib/api-models';
export * from './report-lib/api';
export * from './services';
export * from './util/micro-session';
export * from './util/sign-out-listeners';

const osName = getOSName();

export class Api {
  absenceRequestsApi: AbsenceRequestsApi;
  absencesApi: AbsencesApi;
  accountApi: AccountApi;
  facilityApi: FacilityApi;
  adminApi: AdminApi;
  adminFacilityApi: AdminFacilityApi;
  chatApi: ChatApi;
  departmentApi: DepartmentApi;
  openingTemplateApi: OpeningTemplateApi;
  shiftTypeApi: ShiftTypeApi;
  manageSchedulesApi: ManageSchedulesApi;
  masterScheduleApi: MasterScheduleApi;
  positionApi: PositionApi;
  unitApi: UnitApi;
  hrApi: HrApi;
  schedulePatternApi: SchedulePatternApi;
  metaDataApi: MetaDataApi;
  reportsApi: ReportsApi;
  metricsApi: MetricsApi;
  mobileV2Api: MobileV2Api;
  adminReportsApi: AdminReportsApi;
  openShiftApi: OpenShiftApi;
  laborLevelApi: LaborLevelApi;
  proLaborLevelDesignationApi: ProLaborLevelDesignationApi;
  entityApi: EntityApi;
  employeeV2Api: EmployeeV2Api;
  hourV2Api: HourV2Api;
  punchV2Api: PunchV2Api;
  punchRequestsV2Api: PunchRequestsV2Api;
  timeCardApi: TimeCardApi;
  scheduleV2Api: ScheduleV2Api;
  configurationsV2Api: ConfigurationsV2Api;
  contactSupportV2Api: ContactSupportV2Api;
  timeClockApi: TimeClockApi;

  accrualsV2Api: AccrualsV2Api;
  complianceApi: ComplianceApi;
  dashboardApi: DashboardApi;
  userV2Api: UserV2Api;
  authorizationApi: AuthorizationApi;
  employeeApi: EmployeeApi;

  taReportsReportApi: TaReportsReportApi;
  saaSReportsReportApi: SaaSReportsReportApi;
  pbjApi: PbjApi;
  attachmentsApi: AttachmentsApi;
  toolsV2Api: ToolsV2Api;
  closePayPeriodV2Api: ClosePayPeriodV2Api;
  mobileAppVersionApi: MobileAppVersionApi;
  indexManagementApi: IndexManagementApi;
  importV2Api: ImportV2Api;
  profileManagementApi: ProfileManagementApi;
  supportApi: SupportApi;

  constructor({
    basePath,
    reportsApiUrl,
    accessToken,
    sessionToken,
    environment,
    client,
  }: {
    basePath: string;
    reportsApiUrl: string;
    accessToken: () => string;
    sessionToken: () => string;
    environment: string;
    client: 'portal' | 'admin';
  }) {
    const addTokenTransformRequest: AxiosRequestTransformer = (
      data,
      headers
    ) => {
      headers['Cache-Control'] = 'no-cache';
      headers['client'] = client;
      headers['environment'] = environment;
      headers['os'] = `browser-${osName}`;
      if (headers && accessToken) {
        const token = accessToken();
        headers['Authorization'] = `Bearer ${token}`;
        headers['UserSessionId'] = sessionToken();
        const microSessionId = getCurrentMicroSessionId();
        if (microSessionId) {
          headers['X-Micro-Session-Id'] = microSessionId;
        }
      }
      return data;
    };

    const publicApisConfig = new Configuration({
      basePath,
      baseOptions: { transformRequest: [addTokenTransformRequest] },
    });

    this.accountApi = new AccountApi(publicApisConfig);

    const privateApisConfig = new Configuration({
      basePath,
      baseOptions: { transformRequest: [addTokenTransformRequest] },
    });
    this.facilityApi = new FacilityApi(privateApisConfig);
    this.adminApi = new AdminApi(privateApisConfig);
    this.adminFacilityApi = new AdminFacilityApi(privateApisConfig);
    this.chatApi = new ChatApi(privateApisConfig);

    this.departmentApi = new DepartmentApi(privateApisConfig);
    this.openingTemplateApi = new OpeningTemplateApi(privateApisConfig);
    this.shiftTypeApi = new ShiftTypeApi(privateApisConfig);
    this.unitApi = new UnitApi(privateApisConfig);
    this.manageSchedulesApi = new ManageSchedulesApi(privateApisConfig);
    this.masterScheduleApi = new MasterScheduleApi(privateApisConfig);
    this.positionApi = new PositionApi(privateApisConfig);
    this.hrApi = new HrApi(privateApisConfig);
    this.schedulePatternApi = new SchedulePatternApi(privateApisConfig);
    this.metaDataApi = new MetaDataApi(privateApisConfig);
    this.reportsApi = new ReportsApi(privateApisConfig);
    this.metricsApi = new MetricsApi(privateApisConfig);
    this.mobileV2Api = new MobileV2Api(privateApisConfig);
    this.adminReportsApi = new AdminReportsApi(privateApisConfig);
    this.employeeApi = new EmployeeApi(privateApisConfig);
    this.absenceRequestsApi = new AbsenceRequestsApi(privateApisConfig);
    this.absencesApi = new AbsencesApi(privateApisConfig);
    this.openShiftApi = new OpenShiftApi(privateApisConfig);
    this.laborLevelApi = new LaborLevelApi(privateApisConfig);
    this.proLaborLevelDesignationApi = new ProLaborLevelDesignationApi(
      privateApisConfig
    );
    this.entityApi = new EntityApi(privateApisConfig);
    this.employeeV2Api = new EmployeeV2Api(privateApisConfig);
    this.hourV2Api = new HourV2Api(privateApisConfig);
    this.punchV2Api = new PunchV2Api(privateApisConfig);
    this.punchRequestsV2Api = new PunchRequestsV2Api(privateApisConfig);
    this.timeCardApi = new TimeCardApi(privateApisConfig);
    this.scheduleV2Api = new ScheduleV2Api(privateApisConfig);
    this.configurationsV2Api = new ConfigurationsV2Api(privateApisConfig);
    this.contactSupportV2Api = new ContactSupportV2Api(privateApisConfig);
    this.timeClockApi = new TimeClockApi(privateApisConfig);
    const taPrivateReportApisConfig = new Configuration({
      basePath: reportsApiUrl,
      baseOptions: { transformRequest: [addTokenTransformRequest] },
    });
    this.accrualsV2Api = new AccrualsV2Api(privateApisConfig);
    this.taReportsReportApi = new TaReportsReportApi(taPrivateReportApisConfig);
    this.complianceApi = new ComplianceApi(privateApisConfig);
    this.dashboardApi = new DashboardApi(privateApisConfig);
    this.authorizationApi = new AuthorizationApi(privateApisConfig);
    this.userV2Api = new UserV2Api(privateApisConfig);
    this.pbjApi = new PbjApi(privateApisConfig);
    this.attachmentsApi = new AttachmentsApi(privateApisConfig);
    this.toolsV2Api = new ToolsV2Api(privateApisConfig);
    this.closePayPeriodV2Api = new ClosePayPeriodV2Api(privateApisConfig);
    this.mobileAppVersionApi = new MobileAppVersionApi(privateApisConfig);
    this.indexManagementApi = new IndexManagementApi(privateApisConfig);
    this.importV2Api = new ImportV2Api(privateApisConfig);
    this.profileManagementApi = new ProfileManagementApi(privateApisConfig);
    this.supportApi = new SupportApi(privateApisConfig);
    this.entityApi = new EntityApi(privateApisConfig);

    const reportPrivateApisConfig = new Configuration({
      basePath: reportsApiUrl,
      baseOptions: { transformRequest: [addTokenTransformRequest] },
    });
    this.saaSReportsReportApi = new SaaSReportsReportApi(
      reportPrivateApisConfig
    );

    const disallowedRefreshTokenUrlsPrefix = [
      '/Account/login',
      '/Account/mobile-login',
      '/Account/mobile-register',
      '/Account/refresh-token',
      '/Account/refresh-token-by-token',
      '/Account/validate-username',
      '/Account/v2/register',
      '/Account/v3',
    ];
    const responseInterceptors = new ResponseInterceptors(
      basePath,
      reportsApiUrl,
      disallowedRefreshTokenUrlsPrefix,
      environment,
      client
    );
    globalAxios.interceptors.response.use(
      responseInterceptors.onFulfilled,
      responseInterceptors.onRejected
    );
  }
}

export const useApi = () => {
  const { apiBaseUrl, reportsApiUrl, environment, client } =
    useEnvironmentContext();
  const getAccessToken = useCallback(() => {
    return localStorage.getItem('access_token') as string;
  }, []);

  const getSessionToken = useCallback(() => {
    return localStorage.getItem('session_token') as string;
  }, []);

  const api = useRef(
    new Api({
      basePath: apiBaseUrl,
      reportsApiUrl,
      accessToken: getAccessToken,
      sessionToken: getSessionToken,
      environment,
      client,
    })
  );
  return api.current;
};

export { EnvironmentProvider, useEnvironmentContext };
